/**
 * 注釈の空間索引（§0.1「注釈索引」・U150）。
 * chr1 級（gene/CDS 数万〜十数万件）でも、可視範囲のヒットと間引きを線形走査なしで返す。
 *
 * 実装は固定幅バケット + CSR（バケット境界配列 + 要素配列）。
 * 構築 O(n·バケット跨ぎ)、照会 O(触れたバケット + ヒット数)。
 */

import { Interval, Topology, resolveSpans } from '../sequence/interval';
import { GenomicFeatureV1, featureLength, featureSpans } from './types';

/** バケット数の目安。多すぎると構築が重く、少なすぎると照会が線形に近づく。 */
const TARGET_BUCKET_COUNT = 4096;
const MIN_BUCKET_SIZE = 1024;

export interface FeatureIndexOptions {
  bucketSize?: number;
  /** 照会時の既定位相。未指定時は線形（end>length だけでは環状とみなさない） */
  topology?: Topology;
}

export class FeatureIndex {
  readonly genomeLength: number;
  readonly bucketSize: number;
  readonly bucketCount: number;

  private readonly features: GenomicFeatureV1[];
  /** CSR: バケット i の要素は `entries[offsets[i] .. offsets[i+1])` */
  private readonly offsets: Int32Array;
  private readonly entries: Int32Array;

  private readonly topology: Topology;

  constructor(
    features: GenomicFeatureV1[],
    genomeLength: number,
    options: FeatureIndexOptions = {}
  ) {
    this.features = features;
    this.genomeLength = Math.max(1, genomeLength);
    this.topology = options.topology ?? 'linear';
    this.bucketSize =
      options.bucketSize ??
      Math.max(MIN_BUCKET_SIZE, 2 ** Math.ceil(Math.log2(this.genomeLength / TARGET_BUCKET_COUNT || 1)));
    this.bucketCount = Math.max(1, Math.ceil(this.genomeLength / this.bucketSize));

    const counts = new Int32Array(this.bucketCount + 1);
    const spansPerFeature: Interval[][] = new Array(features.length);

    for (let i = 0; i < features.length; i += 1) {
      const spans = featureSpans(features[i], this.genomeLength);
      spansPerFeature[i] = spans;
      for (const bucket of this.bucketsOf(spans)) counts[bucket] += 1;
    }

    this.offsets = new Int32Array(this.bucketCount + 1);
    let total = 0;
    for (let bucket = 0; bucket < this.bucketCount; bucket += 1) {
      this.offsets[bucket] = total;
      total += counts[bucket];
    }
    this.offsets[this.bucketCount] = total;

    const cursor = Int32Array.from(this.offsets.subarray(0, this.bucketCount));
    this.entries = new Int32Array(total);
    for (let i = 0; i < features.length; i += 1) {
      for (const bucket of this.bucketsOf(spansPerFeature[i])) {
        this.entries[cursor[bucket]] = i;
        cursor[bucket] += 1;
      }
    }
  }

  get size(): number {
    return this.features.length;
  }

  get all(): readonly GenomicFeatureV1[] {
    return this.features;
  }

  /** 区間に重なる注釈（元の並び順）。 */
  query(interval: Interval): GenomicFeatureV1[] {
    return this.queryIndices(interval).map((index) => this.features[index]);
  }

  /** 区間に重なる注釈の件数（間引き判断に使う。配列は作らない） */
  count(interval: Interval): number {
    return this.queryIndices(interval).length;
  }

  /**
   * 描画用。長い順に `limit` 件だけ返す（U150 / U161 の間引き）。
   * 同長なら開始位置順で安定させる。
   */
  queryForRender(interval: Interval, limit: number): GenomicFeatureV1[] {
    const hits = this.query(interval);
    if (hits.length <= limit) return hits;
    return hits
      .slice()
      .sort((a, b) => featureLength(b) - featureLength(a) || a.start - b.start)
      .slice(0, limit);
  }

  /**
   * 1 塩基に当たる注釈。重なりがあるときは短いものを優先する
   * （CDS が gene に包まれているとき CDS を掴める）。
   */
  pick(position: number): GenomicFeatureV1 | null {
    const hits = this.query({ start: position, end: position + 1 });
    if (hits.length === 0) return null;
    return hits.reduce((best, candidate) =>
      featureLength(candidate) < featureLength(best) ? candidate : best
    );
  }

  private queryIndices(interval: Interval): number[] {
    const spans = resolveSpans(
      interval,
      this.genomeLength,
      this.topology === 'circular' || interval.end > this.genomeLength ? 'circular' : 'linear'
    );
    if (spans.length === 0) return [];

    const seen = new Set<number>();
    const result: number[] = [];
    for (const span of spans) {
      const firstBucket = Math.floor(span.start / this.bucketSize);
      const lastBucket = Math.floor(Math.max(span.start, span.end - 1) / this.bucketSize);
      for (let bucket = firstBucket; bucket <= lastBucket && bucket < this.bucketCount; bucket += 1) {
        for (let cursor = this.offsets[bucket]; cursor < this.offsets[bucket + 1]; cursor += 1) {
          const featureIndex = this.entries[cursor];
          if (seen.has(featureIndex)) continue;
          seen.add(featureIndex);
          if (this.overlaps(featureIndex, spans)) result.push(featureIndex);
        }
      }
    }
    result.sort((a, b) => a - b);
    return result;
  }

  private overlaps(featureIndex: number, spans: Interval[]): boolean {
    const featureSpansList = featureSpans(this.features[featureIndex], this.genomeLength);
    return featureSpansList.some((fs) =>
      spans.some((span) => fs.start < span.end && span.start < fs.end)
    );
  }

  private bucketsOf(spans: Interval[]): number[] {
    const buckets: number[] = [];
    for (const span of spans) {
      const first = Math.floor(span.start / this.bucketSize);
      const last = Math.floor(Math.max(span.start, span.end - 1) / this.bucketSize);
      for (let bucket = first; bucket <= last && bucket < this.bucketCount; bucket += 1) {
        if (!buckets.includes(bucket)) buckets.push(bucket);
      }
    }
    return buckets;
  }
}
